import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: 'スマートフォン時代の「当たり前」を提供したい',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/sanu/interview01.webp',
      alt: '左から株式会社SANUの小笠原さん、Aitriaの桐生',
      text: '左から株式会社SANUの小笠原さん、Aitriaの桐生',
    },
    {
      type: 'textBox',
      texts: ['―― プロジェクト前にどのような課題をお持ちでしたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            SANUは創業初期のノーコード運用を経て、昨年4月に内製チームでシステム基盤を刷新しました。少数精鋭のエンジニアとともに「SANUらしい体験を自分たちの手でつくる」べく磨き込んできましたが、Webブラウザだけでは、私たちが理想とする「日常から自然へ軽やかにスイッチする体験」を届けるにはまだ不十分だと感じていたんです。
          </p>
          <p>
            <span className={styles.space_top}>
              というのも、当時はメールボックスからSANUのメールを検索して予約情報を探す手間がありました。プッシュ通知で即座にアクセスできるなど、自然に通う導線をスマホ上で完結させるモバイルアプリならではの体験を、十分に提供できていませんでした。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              私がその提案をしたのが7月の後半ですね。その際、リリースの目標を11月11日に定めました。その日がSANUのサービス開始4周年だったためです。しかし、Webサービス自体の並行開発が続いており、社内リソースが割けない状況でした。そこで、外部パートナーと一緒に開発を進める選択をしました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― その中で、Aitriaを選ばれた決め手は何だったのでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            私たちがすでに使用している技術との親和性が高いReact
            Nativeを採用することを決めていたので、その開発経験が豊富な会社を探そうというのが最初の視点でした。
          </p>
          <p>
            <span className={styles.space_top}>
              Aitriaさんは以前一緒にお仕事した経験があったため、ホームページを拝見したところ、
              <span className={styles.textbold}>React Nativeの開発事例や技術ブログ</span>
              などが多く掲載されていました。そこでお問い合わせさせていただいたのですが、前回別のプロジェクトでご一緒した方に要件を見ていただいた時に「これなら大丈夫です」と言っていただきました。以前の案件で、Aitriaさんは技術力の高さを感じられましたし、今回も自信を持っていろいろと回答いただけたので、信頼できると感じたんです。
            </span>
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '開始前の徹底準備が、短納期を可能に',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/sanu/interview02.webp',
      alt: '開始前の徹底準備が、短納期を可能に',
    },
    {
      type: 'textBox',
      texts: ['―― 実質2か月半という短納期で遅延なくリリースできた要因はどこにあると考えていますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '桐生',
      texts: [
        <>
          <p>
            大きく分けて3つの要因があると考えています。1つ目は、開始1週間前に行った「事前の準備」です。2つ目は、開発初期から「TestFlightによる実機確認」ができる環境を整えたこと。そして3つ目は、柔軟な開発を支える「GraphQLの活用」です。
          </p>
          <p>
            <span className={styles.space_top}>
              これらを軸に、イベントが多く時間を要する通常のスクラム開発ではなく、カンバン方式の軽量アジャイルを採用しました。通常、多くのプロジェクトではファーストリリースに3、4か月ほどかけますが、
              <span className={styles.textbold}>今回は2か月半という短期間での開発</span>だったので、とにかく
              <span className={styles.textbold}>密なコミュニケーションと素早いアウトプットに集中</span>
              したことが、短納期でのリリースに繋がりました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 「事前の準備」では、具体的にどのようなことを行いましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '桐生',
      texts: [
        <>
          <p>
            開始1週間前に、どういう構成で作るかをかっちり準備しました。今回は仕様を作りながら固めていくプロジェクトだったので、デザインやレイアウトが固まるのを待っていては間に合いません。そこで、
            <span className={styles.textbold}>初期段階でディレクトリの設計やライブラリの設定</span>
            を済ませ、決まった時にすぐ変更・反映できるシステム構成を整えました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            そのおかげで、最初の打ち合わせから、「実はもう着手してます」と言っていただき、非常にスムーズなスタートが切れました。
          </p>
          <p>
            <span className={styles.space_top}>
              仕様書も揃っていない、
              <span className={styles.textbold}>仕様やデザインが度々変わるという流動的な状況</span>
              でしたが、皆さん真摯に向き合っていただいて。例えば、ざっくりした仕様から「多分こういうことだろう」と予測して先に実装してもらい、「こんな感じでしょうか」と確認いただく。本来ならこちらから事前に提示するべきことも予想して先行して作っていただくということが結構ありました。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/sanu/interview03.webp',
      alt: '仕様やデザインが度々変わるという流動的な状況',
    },
    {
      type: 'textBox',
      texts: [
        '―― 2つ目に挙げていただいた「TestFlightによる実機確認」というプロセスには、どのようなメリットがありますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '桐生',
      texts: [
        <>
          <p>
            開発初期から実機でのテストを行うことで、実際の利用状況での課題を素早く見つけ、スピーディな対応が可能になります。
          </p>
          <p>
            <span className={styles.space_top}>
              開発者のシミュレーターではなく、ユーザーが普段使っている操作感で使うことで見える、レイアウト崩れや操作感の悪さがありますよね。そのため、開発者以外の第三者が触って気づくことは多いと思っていて。そこで、
              <span className={styles.textbold}>TestFlightで開発者以外の人に配信して触ってもらう環境</span>
              を、出だしの段階で小笠原さんと一緒に作ったんです。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              開発においては<span className={styles.textbold}>実機で動かして操作感を確認して改善を繰り返す</span>
              のが、アプリの品質を向上し、いいUXに近づけていくために必要なことだと考えています。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            それはすごく良かったです。TestFlightによる配信の仕組みは開発者以外でも使えるものであるため、SANUのスタッフにも使ってもらうことを大事なプロセスの1つとして、スケジュールに組み込んで進めました。その結果、開発している私たちでは気づけなかったことを、他のSANUのスタッフが気づくことがたくさんあって。私も実機で触ってみて「ここを修正してほしい」と素早くフィードバックできましたし、ビジネスサイドや社員のみんなと確認を進めました。
          </p>
          <p>
            <span className={styles.space_top}>
              特に今回は、単なる実機テストだけでなく、実際の宿泊施設での検証が不可欠でした。アプリでのチェックイン操作によって暗証番号が表示され、宿泊施設のドアにその番号を入力することで、鍵を開けることができます。他の一般的なスマホアプリとは違って、宿泊施設の鍵と連携して使用することが前提だったので、
              <span className={styles.textbold}>実際のデバイス上で動作する状態にして、施設での動作検証</span>
              を、リリース前に絶対にやりたかったんです。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              この実地検証を事前に計画に組み込んでおいたこともあり、2週間程度テストを行うことができました。実際に使ってみて気づいたこともありましたし、弊社のスタッフにも使ってもらい、フィードバックをもとに改善することをリリース前にできたのは非常に良かったです。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 3つ目の要因である「GraphQLの活用」は、技術的にどう寄与したのでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            もともと、GraphQLはコアの技術として採用していました。最近はメジャーになってきましたが、まだ「GraphQLでお願いします」と言って「はい、わかりました」と言っていただけるベンダーさんは少ないかと思います。
          </p>
          <p>
            <span className={styles.space_top}>
              今回お話したら「全然いけます」ということで。仕様も、仕様書を書いて渡すのではなく、定義されたソースコードを見ていただいて、解釈して作っていただきました。プラスで何かお伝えすることもなく、滞りなく開発を進めていただけたので、非常に良かったです。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              当初、API仕様を理解して実装を進めるのに多少は時間がかかると想定していました。
              <span className={styles.textbold}>GraphQLでの開発経験が豊富にあった</span>
              ことで、スピーディーに開発を進めていただけましたし、短期間でリリースできた大きな勝因だったと思います。
            </span>
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '必要なバリューを届け、ユーザーと共に育てるアプリへ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/sanu/interview04.webp',
      alt: '必要なバリューを届け、ユーザーと共に育てるアプリへ',
    },
    {
      type: 'textBox',
      texts: ['―― Web版と比較してユーザー体験はどのように進化したと感じられていますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            リリースされたモバイルアプリは、最低限のバリューは届けられていると思いますが、まだまだ成長の余地があるという状況です。というのも私たちは、実際にユーザーに使っていただいてフィードバックを得ながら育てていく方が、自分たちがいいと思うものを作るより価値があると考えているからです。
          </p>
          <p>
            <span className={styles.space_top}>
              なので、現状はものすごく作り込んだアプリではありませんが、当初の想定通りユーザーに
              <span className={styles.textbold}>「モバイルアプリとしての使い勝手の良さ」</span>
              という価値は届けられたと思っています。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― リリース後の反響はいかがでしょうか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            なかなかいい反響って届きにくいんですが、「こうしてほしい」「Android版も作ってください」「こういう機能があったら嬉しい」というフィードバックをいただいています。期待は大きいと感じています。
          </p>
          <p>
            <span className={styles.space_top}>
              スタッフは皆「待ってました」という感じで、非常に喜んでくれました。SANUのスタッフはSANUのヘビーユーザーでもあって、みんな定期的に滞在しています。「アプリは出さないの？」という声もあったので、「アプリが出てよかった」と言ってくれたのは純粋に嬉しいですね。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              それから、誰もこんな短期間でリリースするとは思ってなかったみたいで、「もうできたの！？」という反応もいただきました（笑）。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― 将来的に内製化を目指されていると伺いました。その中で、今回の協働開発はどのような意義がありましたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            私たちはReactやGraphQLを活用した開発を行っていますが、
            <span className={styles.textbold}>React Nativeでのアプリ開発経験</span>
            のあるエンジニアがおらず、限られたリソースで1から作るのは難しかったと思います。
          </p>
          <p>
            <span className={styles.space_top}>
              今回、React
              Native開発の豊富な経験をお持ちのAitriaさんと一緒に開発できたことで、SANUのアプリ開発の土台が出来上がり、私たち自身でスムーズにアプリ開発を進めていくことができるようになったと感じています。
            </span>
          </p>
          <p>
            <span className={styles.space_top}>
              Aitriaさんは、今回モバイルアプリ開発だけ対応いただきましたが、以前にWebアプリケーション開発や、UX/UIデザインもご一緒させていただきました。ですので、デザインから開発までをサービス単位でまるっとお任せできる可能性もあるのかなと思っています。SANUが成長していく中で、そのような機会があれば、またぜひご一緒できればと思っています。
            </span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '桐生',
      texts: [
        <>
          <p>
            こちらこそ、今回はご一緒できて嬉しかったです。始まる前は2か月半でリリースできるのか不安もありましたが、やろうと思えばできるという良い経験になりました。開発チームに加わっていた若いエンジニア達も「仕事が楽しい」と言ってくれて。そういった面も含めて、いろんな意味で良い環境で仕事ができたと思います。ありがとうございました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '小笠原さん',
      texts: [
        <>
          <p>
            本当に、皆さんと楽しく仕事をすることができました。そのことが一番良かったです。こちらこそありがとうございました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/sanu/interview05.webp',
      alt: '小笠原さんと桐生',
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    const timeline = gsap.timeline()
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    const trigger = ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })

    return () => {
      trigger.kill()
      timeline.kill()
    }
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
